import type { ComponentProps } from 'react'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { MAX_CAROUSEL_ITEM_COUNT } from './common/constants'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { ALL_HOTEL_TYPES, LE_CONTRACTED_HOTEL_TYPES } from 'constants/offer'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import qs from 'qs'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { encodeSearchParams } from 'lib/search/searchUtils'
import config from 'constants/config'
import { useIntl } from 'react-intl'
import useOfferList from 'hooks/Offers/useOfferList'
import { useAppSelector } from 'hooks/reduxHooks'
import { getOfferListKeyForUser } from 'lib/offer/offerListKey'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import holdHotelOfferListUntilStreamLimit from './holdHotelOfferListUntilStreamLimit'

const DEFAULT_SEARCH_ROOMS: Array<App.Occupants> = [config.search.defaultOccupants]

interface Props extends Pick<ComponentProps<typeof OfferCardCarouselSection>, 'tileStyle' | 'tileSize' | 'minimumOffersCount' | 'preferListingOffers'> {
  sortBy?: 'value'
  title?: React.ReactNode
  defaultFilter?: string
  offerTypes?: Array<App.OfferType>
  destinationId?: string
  viewAllUrl?: string
  viewSomethingElseUrl?: string
  streamList?: boolean
  /** When set with streamList, keep fetching until this many ids arrive so the row does not claim a short first batch. */
  holdUntilLimit?: boolean
  hideFilters?: boolean
  additionalFilters?: ComponentProps<typeof OfferCardCarouselSection>['additionalFilters']
  additionalFilterOptions?: ComponentProps<typeof OfferCardCarouselSection>['additionalFilterOptions']
}

function HotelOfferCarousel(props: Props) {
  const {
    sortBy,
    title,
    tileStyle = 'card-default',
    tileSize = 'medium',
    preferListingOffers = false,
    defaultFilter,
    offerTypes,
    destinationId,
    viewAllUrl,
    streamList,
    holdUntilLimit = false,
    hideFilters = false,
    additionalFilters,
    additionalFilterOptions,
    minimumOffersCount,
  } = props
  const intl = useIntl()
  const resolvedAdditionalFilters = hideFilters ? undefined : (additionalFilters ?? 'holidayTypes')
  const isManualSortingEnabled = isManualSortingEnabledFor('carousel-hotel')

  const filters: App.OfferListFilters = useMemo(() => {
    if (isManualSortingEnabled && !sortBy) {
      return {
        offerTypes: ALL_HOTEL_TYPES,
        sortBy: 'carousel-hotel',
        limit: MAX_CAROUSEL_ITEM_COUNT,
        isStream: !!streamList,
        ...(offerTypes && { offerTypes }),
        ...(destinationId && { destinationId }),
      }
    } else {
      return {
        offerTypes: LE_CONTRACTED_HOTEL_TYPES,
        frontPageVisibleOnly: true,
        limit: MAX_CAROUSEL_ITEM_COUNT,
        isStream: !!streamList,
        sortBy,
      }
    }
  }, [isManualSortingEnabled, sortBy, streamList, offerTypes, destinationId])

  const defaultSearchUrl = useMemo(() => {
    const params = encodeSearchParams({
      searchItem: ANYWHERE_SEARCH_ITEM,
      rooms: DEFAULT_SEARCH_ROOMS,
      isFlexibleWithDate: true,
    })
    const query = params.toString()
    return `/search${query ? `?${query}` : ''}`
  }, [])

  const to = useMemo(() => {
    if (viewAllUrl) {
      return viewAllUrl
    }
    if (sortBy) {
      const query = { sortBy }
      return `/hotels?${qs.stringify(query, { arrayFormat: 'comma' })}`
    }
    return defaultSearchUrl
  }, [defaultSearchUrl, sortBy, viewAllUrl])

  const baseViewAllLink = useMemo(() => {
    if (viewAllUrl) {
      return viewAllUrl
    }
    if (sortBy) {
      return '/hotels'
    }
    return defaultSearchUrl
  }, [defaultSearchUrl, sortBy, viewAllUrl])

  const headingTitle = useMemo(() => {
    if (sortBy) {
      return intl.formatMessage(
        {
          id: 'VR-p-PfEKMNzVzg7RPqd',
          defaultMessage: 'Best value <i>hotel</i> offers',
          description: 'Title for best value hotel carousel with italicized hotel',
        },
        {
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        },
      )
    }
    return intl.formatMessage(
      {
        id: 'ixtLdHdYVBPbNX58RzFC',
        defaultMessage: 'Limited-time <i>hotel</i> offers',
        description: 'Title for limited-time hotel carousel with italicized hotel',
      },
      {
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    )
  }, [intl, sortBy])

  const streamHoldCount = holdUntilLimit && streamList ? filters.limit : undefined
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const streamListKey = getOfferListKeyForUser(filters, canViewLuxPlusBenefits)
  const rawStreamList = useAppSelector(state => state.offer.offerLists[streamListKey])
  const fetchedStreamList = useOfferList(filters, {
    disabled: !streamHoldCount,
    minRequiredOffers: streamHoldCount,
    preferListingOffers,
  })
  const streamHoldOfferList = holdHotelOfferListUntilStreamLimit(
    fetchedStreamList,
    rawStreamList,
    streamHoldCount,
  )

  const buildViewAllLink = useCallback((selectedFilter?: string) => {
    if (!selectedFilter) {
      return baseViewAllLink
    }

    const targetLink = baseViewAllLink.startsWith('/search') ? baseViewAllLink : defaultSearchUrl
    const [pathname, queryString = ''] = targetLink.split('?')
    const params = new URLSearchParams(queryString)
    params.set('holidayTypes', selectedFilter)
    const nextQuery = params.toString()
    return nextQuery ? `${pathname}?${nextQuery}` : pathname
  }, [baseViewAllLink, defaultSearchUrl])

  return (
    <OfferCardCarouselSection
      preferListingOffers={preferListingOffers}
      filters={filters}
      title={title ?? headingTitle}
      allLink={baseViewAllLink}
      buildAllLink={buildViewAllLink}
      additionalFilters={resolvedAdditionalFilters}
      additionalFilterOptions={additionalFilterOptions}
      tileStyle={tileStyle}
      tileSize={tileSize}
      defaultFilter={defaultFilter}
      minimumOffersCount={minimumOffersCount}
      overrideOfferList={streamHoldOfferList}
      data-testid="hotel-carousel"
    >
      <OfferCarouselLookingForSomethingElse to={to} />
    </OfferCardCarouselSection>
  )
}

export default React.memo(HotelOfferCarousel)
